import { PerguntaFormulario, RespostaFormulario, BlocoPerguntas, ConclusaoAutomatica } from '@/types/tarefas';

const ETAPA_LABELS: Record<string, string> = {
  'lead-capturado': 'Lead Capturado',
  'qualificacao': 'Qualificação',
  'analise-necessidades': 'Análise de Necessidades',
  'visita-tecnica': 'Visita Técnica',
  'proposta-valor': 'Proposta de Valor',
  'negocio-fechado': 'Negócio Fechado',
  'follow-up': 'Follow-up',
  'leads-perdidos': 'Leads Perdidos',
  'cliente-servico': 'Cliente / Serviço',
  'outras': 'Outras',
};

export const getEtapaNome = (etapa: string) => ETAPA_LABELS[etapa] || etapa;

export const formatDate = (dateStr: string | undefined) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatPhone = (phone: string | undefined) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

/**
 * Expande perguntas do template com perguntas de blocos referenciados
 * Quando uma pergunta ou regra tem proximo_bloco_id, insere as perguntas do bloco
 */
export const expandirPerguntasComBlocos = (
  perguntas: PerguntaFormulario[],
  blocosMap: Map<string, BlocoPerguntas>
): PerguntaFormulario[] => {
  const perguntasExpandidas: PerguntaFormulario[] = [];
  const ordemMaxima = Math.max(...perguntas.map(p => p.ordem || 0), 0);
  let offsetOrdem = ordemMaxima + 1;

  for (const pergunta of perguntas) {
    perguntasExpandidas.push(pergunta);

    // Verificar se a pergunta tem proximo_bloco_id
    if (pergunta.proximo_bloco_id) {
      const bloco = blocosMap.get(pergunta.proximo_bloco_id);
      if (bloco && bloco.perguntas && bloco.perguntas.length > 0) {
        // Inserir perguntas do bloco após a pergunta atual
        const perguntasBloco = bloco.perguntas.map((p, index) => ({
          ...p,
          ordem: (pergunta.ordem || 0) + index + 1
        }));
        perguntasExpandidas.push(...perguntasBloco);
        offsetOrdem = Math.max(...perguntasBloco.map(p => p.ordem || 0), offsetOrdem) + 1;
      }
    }

    // Verificar regras de navegação que apontam para blocos
    if (Array.isArray(pergunta.regras_navegacao)) {
      for (const regra of pergunta.regras_navegacao) {
        if (regra.proximo_bloco_id) {
          const bloco = blocosMap.get(regra.proximo_bloco_id);
          if (bloco && bloco.perguntas && bloco.perguntas.length > 0) {
            // Verificar se as perguntas do bloco já foram inseridas
            const primeiraPerguntaBloco = bloco.perguntas[0];
            if (!perguntasExpandidas.find(p => p.id === primeiraPerguntaBloco.id)) {
              const perguntasBloco = bloco.perguntas.map((p, index) => ({
                ...p,
                ordem: offsetOrdem + index
              }));
              perguntasExpandidas.push(...perguntasBloco);
              offsetOrdem = Math.max(...perguntasBloco.map(p => p.ordem || 0), offsetOrdem) + 1;
            }
          }
        }
      }
    }
  }

  return perguntasExpandidas;
};

/**
 * Calcula o fluxo de perguntas baseado nas respostas existentes
 * Seguindo as regras de navegação definidas no template
 * Agora suporta blocos através de proximo_bloco_id
 */
export const calcularFluxoPerguntas = (
  perguntas: PerguntaFormulario[],
  respostasMap: Record<string, string>,
  blocosMap?: Map<string, BlocoPerguntas>
): string[] => {
  if (!perguntas.length) {
    return [];
  }

  // Expandir perguntas com blocos se blocosMap for fornecido
  const perguntasParaProcessar = blocosMap 
    ? expandirPerguntasComBlocos(perguntas, blocosMap)
    : perguntas;

  const ordenadas = [...perguntasParaProcessar].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  const mapaPerguntas = new Map(ordenadas.map((pergunta) => [pergunta.id, pergunta]));
  const destinosReferenciados = new Set<string>();

  // Identificar perguntas que são referenciadas como destino
  ordenadas.forEach((pergunta) => {
    const destinoPadrao = pergunta.proxima_pergunta_id?.trim();
    if (destinoPadrao && destinoPadrao !== 'fim') {
      destinosReferenciados.add(destinoPadrao);
    }

    if (Array.isArray(pergunta.regras_navegacao)) {
      pergunta.regras_navegacao.forEach((regra) => {
        const destinoCondicional = regra.proxima_pergunta_id?.trim();
        if (destinoCondicional && destinoCondicional !== 'fim') {
          destinosReferenciados.add(destinoCondicional);
        }
        // Se a regra aponta para um bloco, marcar a primeira pergunta do bloco como destino
        if (regra.proximo_bloco_id && blocosMap) {
          const bloco = blocosMap.get(regra.proximo_bloco_id);
          if (bloco && bloco.perguntas && bloco.perguntas.length > 0) {
            destinosReferenciados.add(bloco.perguntas[0].id);
          }
        }
      });
    }

    // Se a pergunta aponta para um bloco, marcar a primeira pergunta do bloco como destino
    if (pergunta.proximo_bloco_id && blocosMap) {
      const bloco = blocosMap.get(pergunta.proximo_bloco_id);
      if (bloco && bloco.perguntas && bloco.perguntas.length > 0) {
        destinosReferenciados.add(bloco.perguntas[0].id);
      }
    }
  });

  // Encontrar a primeira pergunta (aquela que não é referenciada como destino)
  const primeiraPergunta =
    ordenadas.find((pergunta) => !destinosReferenciados.has(pergunta.id)) || ordenadas[0];

  const fluxo: string[] = [];
  const visitadas = new Set<string>();

  const normalizarResposta = (valor?: string) => {
    if (!valor) {
      return '';
    }
    return valor.trim().toLowerCase();
  };

  const buscarProximaPorOrdem = (
    perguntaAtual: PerguntaFormulario,
    visitadasSet: Set<string>
  ): string | undefined => {
    const ordemAtual = perguntaAtual.ordem || 0;
    const proximasPossiveis = ordenadas.filter(
      (pergunta) => (pergunta.ordem || 0) > ordemAtual && !visitadasSet.has(pergunta.id)
    );
    return proximasPossiveis.length > 0 ? proximasPossiveis[0].id : undefined;
  };

  let atual: PerguntaFormulario | undefined = primeiraPergunta;

  while (atual && !visitadas.has(atual.id)) {
    fluxo.push(atual.id);
    visitadas.add(atual.id);

    if (atual.finaliza_fluxo) {
      break;
    }

    const respostaAtual = respostasMap[atual.id];
    const respostaAtualNormalizada = normalizarResposta(respostaAtual);

    // Se há uma resposta para esta pergunta, verificar se ela leva ao fim do fluxo ou a um bloco
    if (respostaAtualNormalizada && Array.isArray(atual.regras_navegacao)) {
      const regraCorrespondente = atual.regras_navegacao.find(
        (regra) => normalizarResposta(regra.resposta) === respostaAtualNormalizada
      );

      if (regraCorrespondente) {
        // Se a regra leva ao fim do fluxo, parar aqui
        if (!regraCorrespondente.proxima_pergunta_id && !regraCorrespondente.proximo_bloco_id) {
          break;
        }
        if (regraCorrespondente.proxima_pergunta_id === 'fim') {
          break;
        }

        // Se a regra aponta para um bloco, ir para a primeira pergunta do bloco
        if (regraCorrespondente.proximo_bloco_id && blocosMap) {
          const bloco = blocosMap.get(regraCorrespondente.proximo_bloco_id);
          if (bloco && bloco.perguntas && bloco.perguntas.length > 0) {
            const primeiraPerguntaBloco = bloco.perguntas[0];
            if (!visitadas.has(primeiraPerguntaBloco.id)) {
              atual = primeiraPerguntaBloco;
              continue;
            }
          }
          break;
        }

        // Se a regra tem uma próxima pergunta específica, ir para ela
        if (regraCorrespondente.proxima_pergunta_id && regraCorrespondente.proxima_pergunta_id.trim() !== '') {
          const proximaId = regraCorrespondente.proxima_pergunta_id.trim();
          const proximaPergunta = mapaPerguntas.get(proximaId);
          if (proximaPergunta && !visitadas.has(proximaPergunta.id)) {
            atual = proximaPergunta;
            continue;
          }
          break;
        }
      }
    }

    // Se não há resposta ou a resposta não leva ao fim, verificar proximo_bloco_id da pergunta
    if (atual.proximo_bloco_id && blocosMap) {
      const bloco = blocosMap.get(atual.proximo_bloco_id);
      if (bloco && bloco.perguntas && bloco.perguntas.length > 0) {
        const primeiraPerguntaBloco = bloco.perguntas[0];
        if (!visitadas.has(primeiraPerguntaBloco.id)) {
          atual = primeiraPerguntaBloco;
          continue;
        }
      }
    }

    // Continuar por ordem ou proxima_pergunta_id
    let proximaId = atual.proxima_pergunta_id?.trim();
    if (!proximaId || proximaId === 'fim') {
      proximaId = buscarProximaPorOrdem(atual, visitadas);
    }

    if (!proximaId) {
      break;
    }

    const proximaPergunta = mapaPerguntas.get(proximaId);
    if (!proximaPergunta || visitadas.has(proximaPergunta.id)) {
      break;
    }

    atual = proximaPergunta;
  }

  return fluxo;
};

/**
 * Converte respostas de formulário em um mapa de respostas
 */
export const respostasParaMapa = (respostas: RespostaFormulario[]): Record<string, string> => {
  return respostas.reduce((acc, resposta) => {
    acc[resposta.pergunta_id] = resposta.resposta;
    return acc;
  }, {} as Record<string, string>);
};

/**
 * Determina a próxima pergunta baseada na resposta dada e nas regras do template
 * Esta função identifica a resposta fornecida pelo usuário e navega corretamente
 * para a próxima pergunta de acordo com a lógica definida no template
 * Agora suporta blocos através de proximo_bloco_id
 */
export const determinarProximaPergunta = (
  perguntaAtual: PerguntaFormulario,
  respostaDada: string,
  todasPerguntas: PerguntaFormulario[],
  blocosMap?: Map<string, BlocoPerguntas>
): string | null => {
  console.log('🔍 [DEBUG] determinarProximaPergunta chamada:', {
    perguntaAtual: {
      id: perguntaAtual.id,
      texto: perguntaAtual.texto,
      finaliza_fluxo: perguntaAtual.finaliza_fluxo,
      proxima_pergunta_id: perguntaAtual.proxima_pergunta_id,
      proximo_bloco_id: perguntaAtual.proximo_bloco_id,
      regras_navegacao: perguntaAtual.regras_navegacao
    },
    respostaDada,
    totalPerguntas: todasPerguntas.length
  });

  // Normalizar a resposta para comparação case-insensitive
  const respostaNormalizada = respostaDada ? respostaDada.trim().toLowerCase() : '';

  console.log('🔍 [DEBUG] Resposta normalizada:', respostaNormalizada);

  // Se a pergunta atual finaliza o fluxo, não há próxima pergunta
  if (perguntaAtual.finaliza_fluxo) {
    console.log('🔍 [DEBUG] Pergunta finaliza fluxo - retornando null');
    return null;
  }

  // Primeiro, verificar regras de navegação condicional
  if (Array.isArray(perguntaAtual.regras_navegacao) && respostaNormalizada) {
    console.log('🔍 [DEBUG] Verificando regras de navegação condicional:', perguntaAtual.regras_navegacao);

    // Procurar por uma regra que corresponda exatamente à resposta dada
    const regraCorrespondente = perguntaAtual.regras_navegacao.find(
      (regra) => {
        const respostaRegra = regra.resposta ? regra.resposta.trim().toLowerCase() : '';
        console.log('🔍 [DEBUG] Comparando respostas:', {
          respostaRegra,
          respostaNormalizada,
          match: respostaRegra === respostaNormalizada
        });
        return respostaRegra === respostaNormalizada;
      }
    );

    console.log('🔍 [DEBUG] Regra correspondente encontrada:', regraCorrespondente);

    if (regraCorrespondente) {
      // Se a regra aponta para um bloco, retornar a primeira pergunta do bloco
      if (regraCorrespondente.proximo_bloco_id && blocosMap) {
        const bloco = blocosMap.get(regraCorrespondente.proximo_bloco_id);
        if (bloco && bloco.perguntas && bloco.perguntas.length > 0) {
          const primeiraPerguntaBloco = bloco.perguntas[0];
          console.log('🔍 [DEBUG] Regra aponta para bloco, retornando primeira pergunta do bloco:', primeiraPerguntaBloco.id);
          return primeiraPerguntaBloco.id;
        }
      }

      // Se a regra tem proxima_pergunta_id e não é "fim", navegar para ela
      if (regraCorrespondente.proxima_pergunta_id && regraCorrespondente.proxima_pergunta_id.trim() !== '' && regraCorrespondente.proxima_pergunta_id.trim() !== 'fim') {
        const proximaId = regraCorrespondente.proxima_pergunta_id.trim();
        console.log('🔍 [DEBUG] Tentando navegar para pergunta ID:', proximaId);

        // Verificar se a pergunta de destino existe
        const perguntaDestino = todasPerguntas.find(p => p.id === proximaId);
        console.log('🔍 [DEBUG] Pergunta de destino encontrada:', perguntaDestino ? 'SIM' : 'NÃO');

        if (perguntaDestino) {
          console.log('🔍 [DEBUG] Retornando pergunta condicional:', proximaId);
          return proximaId;
        } else {
          console.log('🔍 [DEBUG] Pergunta de destino não existe - ignorando');
        }
      } else if (!regraCorrespondente.proximo_bloco_id) {
        // Se a regra tem "fim" ou não tem proxima_pergunta_id nem proximo_bloco_id, significa que finaliza o fluxo
        console.log('🔍 [DEBUG] Regra indica fim do fluxo');
        return null;
      }
    }
  } else {
    console.log('🔍 [DEBUG] Não há regras condicionais ou resposta está vazia');
  }

  // Se não há regras condicionais ou nenhuma correspondeu,
  // verificar destino padrão da pergunta atual
  if (perguntaAtual.proximo_bloco_id && blocosMap) {
    const bloco = blocosMap.get(perguntaAtual.proximo_bloco_id);
    if (bloco && bloco.perguntas && bloco.perguntas.length > 0) {
      const primeiraPerguntaBloco = bloco.perguntas[0];
      console.log('🔍 [DEBUG] Pergunta aponta para bloco, retornando primeira pergunta do bloco:', primeiraPerguntaBloco.id);
      return primeiraPerguntaBloco.id;
    }
  }

  if (perguntaAtual.proxima_pergunta_id) {
    const proximaId = perguntaAtual.proxima_pergunta_id.trim();
    console.log('🔍 [DEBUG] Verificando destino padrão:', proximaId);

    const perguntaDestino = todasPerguntas.find(p => p.id === proximaId);
    console.log('🔍 [DEBUG] Pergunta padrão encontrada:', perguntaDestino ? 'SIM' : 'NÃO');

    if (perguntaDestino) {
      console.log('🔍 [DEBUG] Retornando pergunta padrão:', proximaId);
      return proximaId;
    } else {
      console.log('🔍 [DEBUG] Pergunta padrão não existe - ignorando');
    }
  } else {
    console.log('🔍 [DEBUG] Não há destino padrão definido');
  }

  // Se não há destino definido, tentar encontrar a próxima pergunta por ordem
  const ordemAtual = perguntaAtual.ordem || 0;
  console.log('🔍 [DEBUG] Procurando próxima pergunta por ordem. Ordem atual:', ordemAtual);

  const proximasPossiveis = todasPerguntas
    .filter(pergunta => (pergunta.ordem || 0) > ordemAtual)
    .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

  console.log('🔍 [DEBUG] Próximas perguntas possíveis encontradas:', proximasPossiveis.length);
  console.log('🔍 [DEBUG] Próximas perguntas:', proximasPossiveis.map(p => ({ id: p.id, ordem: p.ordem, texto: p.texto.substring(0, 50) })));

  const resultado = proximasPossiveis.length > 0 ? proximasPossiveis[0].id : null;
  console.log('🔍 [DEBUG] Resultado final da função:', resultado);

  return resultado;
};

/**
 * Calcula estatísticas do fluxo de perguntas
 */
export const calcularEstatisticasFluxo = (
  fluxo: string[],
  respostas: Record<string, string>,
  perguntas: PerguntaFormulario[]
) => {
  const respondidas = new Set<string>();
  Object.entries(respostas).forEach(([id, valor]) => {
    if (valor && valor.trim() !== '') {
      respondidas.add(id);
    }
  });

  const primeiraNaoRespondida = fluxo.find((id) => !respondidas.has(id));
  const progresso = fluxo.length > 0 ? (respondidas.size / fluxo.length) * 100 : 0;

  return {
    totalPerguntas: fluxo.length,
    respondidas: respondidas.size,
    naoRespondidas: fluxo.length - respondidas.size,
    progresso: Math.round(progresso),
    primeiraNaoRespondida,
    todasRespondidas: respondidas.size === fluxo.length
  };
};

/**
 * Avalia as conclusões automáticas baseadas nas respostas fornecidas
 * Retorna o nome da conclusão que corresponde à pergunta de fim respondida, ou null se nenhuma corresponder
 * 
 * IMPORTANTE: A conclusão é aplicada quando pelo menos UMA pergunta de fim (finaliza_fluxo) respondida
 * está presente na lista de pergunta_ids da conclusão. Não é necessário que todas as perguntas da conclusão sejam respondidas.
 * 
 * @param conclusoes Lista de conclusões automáticas definidas no template
 * @param respostasMap Mapa de respostas (pergunta_id -> resposta)
 * @param perguntasFormulario Lista de todas as perguntas do formulário (para identificar perguntas de fim)
 * @returns Nome da conclusão que corresponde ou null
 */
export const avaliarConclusoesAutomaticas = (
  conclusoes: ConclusaoAutomatica[] | undefined,
  respostasMap: Record<string, string>,
  perguntasFormulario?: PerguntaFormulario[]
): string | null => {
  if (!conclusoes || conclusoes.length === 0) {
    console.log('🔍 avaliarConclusoesAutomaticas: Nenhuma conclusão definida');
    return null;
  }

  // Obter IDs das perguntas que foram respondidas (com resposta não vazia)
  const perguntasRespondidas = new Set(
    Object.entries(respostasMap)
      .filter(([_, resposta]) => resposta && resposta.trim() !== '')
      .map(([perguntaId]) => perguntaId)
  );

  // Filtrar apenas perguntas de fim que foram respondidas
  let perguntasFimRespondidas: Set<string> = new Set();
  if (perguntasFormulario && perguntasFormulario.length > 0) {
    perguntasFimRespondidas = new Set(
      perguntasFormulario
        .filter(p => p.finaliza_fluxo === true && perguntasRespondidas.has(p.id))
        .map(p => p.id)
    );
  } else {
    // Se não tivermos a lista de perguntas, usar todas as respondidas como fallback
    // (não ideal, mas mantém compatibilidade)
    perguntasFimRespondidas = perguntasRespondidas;
  }

  console.log('🔍 avaliarConclusoesAutomaticas: Todas perguntas respondidas:', Array.from(perguntasRespondidas));
  console.log('🔍 avaliarConclusoesAutomaticas: Perguntas de FIM respondidas:', Array.from(perguntasFimRespondidas));
  console.log('🔍 avaliarConclusoesAutomaticas: Conclusões para avaliar:', conclusoes.map(c => ({
    nome: c.nome,
    pergunta_ids: c.pergunta_ids.length,
    temPerguntaFimRespondida: c.pergunta_ids.some(id => perguntasFimRespondidas.has(id))
  })));

  // Verificar cada conclusão para ver se pelo menos uma pergunta de fim respondida está na lista
  for (const conclusao of conclusoes) {
    // Verificar se pelo menos uma pergunta de fim respondida está na lista de pergunta_ids da conclusão
    const temPerguntaFimCorrespondente = conclusao.pergunta_ids.length > 0 && conclusao.pergunta_ids.some(
      perguntaId => perguntasFimRespondidas.has(perguntaId)
    );

    if (temPerguntaFimCorrespondente) {
      const perguntasCorrespondentes = conclusao.pergunta_ids.filter(id => perguntasFimRespondidas.has(id));
      console.log('✅ avaliarConclusoesAutomaticas: Conclusão encontrada:', conclusao.nome, '- Perguntas de fim correspondentes:', perguntasCorrespondentes);
      return conclusao.nome;
    } else {
      const perguntasFimNaConclusao = conclusao.pergunta_ids.filter(id => perguntasFimRespondidas.has(id));
      console.log(`❌ avaliarConclusoesAutomaticas: Conclusão "${conclusao.nome}" não aplicável. Nenhuma pergunta de fim respondida está na lista da conclusão. Perguntas de fim da conclusão:`, conclusao.pergunta_ids);
    }
  }

  console.log('❌ avaliarConclusoesAutomaticas: Nenhuma conclusão aplicável encontrada');
  return null;
};

